import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import type { TaskBoard } from '@shared/types/tasks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { attachFilesToTask, withinSizeLimit } from './task-files';
import { useFileDrop } from '@/components/useFileDrop';
import { cn } from '@/lib/utils';
import { clipboardFiles, pendingKey } from './clipboard-files';
import { PendingFiles, type PendingFile } from './PendingFiles';
import { MentionTextarea } from './MentionTextarea';
import { displayToMarkup, type PickedMention } from './mention-text';
import { ContextPicker } from './TaskPickers';
import { ContextChip, LabelChip } from './task-ui';
import { TaskFieldControls } from './TaskFieldControls';
import { createTaskNow, type DraftContext } from './task-create';
import { capturePage, readAttachScreenshot, saveAttachScreenshot } from './page-screenshot';
import { useIsPhone, useVisibleArea } from '@/hooks/use-visual-viewport';
import { LoadingState } from '@/components/LoadingState';
import { LoadError } from '@/components/LoadError';
import { describeApiError } from '@/lib/api-errors';
import { useCurrentProject } from '@/lib/current-project';
import { useTaskMeta } from './useTasks';

export type { DraftContext } from './task-create';

type NewTaskProps = {
  meta: Pick<TaskBoard, 'members' | 'labels' | 'columns' | 'types'>;
  initialContexts?: DraftContext[];
  /** Filled in when the form opens from a suggestion. */
  initialTitle?: string;
  initialText?: string;
  currentPage?: DraftContext | null;
  onCreated?: (taskId: string) => void;
  /** The heading; "New task" when left out ("New task in Software Factory" on the master board). */
  heading?: string;
};

/** Create a task, optionally pre-linked to the page you're on. */
/** The type choice that leaves it to Jev. */
const DEFAULT_TYPE = 'default';

/** A Board with nothing on it, while the picked project's loads: the form keeps what's typed meanwhile. */
const NO_BOARD: NewTaskProps['meta'] = { members: [], labels: [], columns: [], types: [] };

export function NewTaskDialog({
  open,
  onOpenChange,
  meta: givenMeta,
  projectId,
  projectPicker,
  ...props
}: Omit<NewTaskProps, 'meta'> & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The project's Board, when the page has it; null while it loads. Left out, it's the tab's project's
   * cached task meta (SF-T-172), shown at once when cached.
   */
  meta?: NewTaskProps['meta'] | null;
  /** With the cached meta: the project the task goes in. Its meta counts only once it's the tab's project. */
  projectId?: string;
  /**
   * SF-T-135: a page outside projects (the Inbox) lets you pick where the task goes, under the heading.
   * Picking another project (`projectKey`) keeps the title, description and files, and clears that
   * Board's own choices (column, type, people, labels). `status` replaces the Board's fields while it
   * loads or fails to (a LoadError with Retry).
   */
  projectPicker?: { node: ReactNode; projectKey: string; status?: ReactNode };
}) {
  const phone = useIsPhone();
  const visible = useVisibleArea(open && phone);
  const current = useCurrentProject();
  const fromCache = givenMeta === undefined;
  const inScope = projectId === undefined || current?.id === projectId;
  const cached = useTaskMeta(open && fromCache && inScope);
  const meta = fromCache ? (inScope ? (cached.data?.data ?? null) : null) : givenMeta;
  const cacheError =
    fromCache && inScope && !meta && cached.data?.error ? (
      <LoadError compact title="Couldn’t load this project’s Board" error={describeApiError(cached.data)} onRetry={cached.reload} />
    ) : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Phones: near the top, not centred, so the keyboard the title opens doesn't cover it, and
        // no taller than what the keyboard leaves, scrolling inside (T-35).
        className="sm:max-w-xl max-sm:top-4 max-sm:translate-y-0"
        style={phone && visible ? { top: visible.offsetTop + 16, maxHeight: visible.height - 32 } : undefined}
      >
        {/* The dialog's content unmounts when it closes, so each opening starts a blank form. */}
        {projectPicker ? (
          <>
            <DialogHeader>
              <DialogTitle>{props.heading ?? 'New task'}</DialogTitle>
            </DialogHeader>
            {projectPicker.node}
            <NewTaskForm
              {...props}
              meta={meta ?? NO_BOARD}
              projectKey={projectPicker.projectKey}
              boardStatus={meta ? null : (projectPicker.status ?? cacheError ?? <LoadingState label="Loading the Board…" />)}
              hideHeading
              onClose={() => onOpenChange(false)}
            />
          </>
        ) : meta ? (
          <NewTaskForm {...props} meta={meta} onClose={() => onOpenChange(false)} />
        ) : (
          (cacheError ?? <LoadingState label="Loading the Board…" />)
        )}
      </DialogContent>
    </Dialog>
  );
}

function NewTaskForm({
  meta,
  initialContexts = [],
  initialTitle = '',
  initialText = '',
  currentPage,
  onCreated,
  heading = 'New task',
  hideHeading = false,
  projectKey,
  boardStatus = null,
  onClose,
}: NewTaskProps & {
  onClose: () => void;
  /** The dialog shows it, above the project picker. */
  hideHeading?: boolean;
  /** The picked project (SF-T-135); a change clears the old Board's choices, keeping the rest. */
  projectKey?: string;
  /** Shown instead of the Board's fields while its Board loads or fails to; Create waits for it. */
  boardStatus?: ReactNode;
}) {
  const firstOpen = meta.columns.find((c) => !c.is_done) ?? meta.columns[0];
  const [title, setTitle] = useState(initialTitle);
  const [text, setText] = useState(initialText);
  const [mentions, setMentions] = useState<PickedMention[]>([]);
  const [columnId, setColumnId] = useState<string | undefined>(firstOpen?.id);
  const [assignees, setAssignees] = useState<string[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  // Left on Default type, the task gets the board's default type.
  const [typeId, setTypeId] = useState<string>(DEFAULT_TYPE);
  const [contexts, setContexts] = useState<DraftContext[]>(initialContexts);
  const [files, setFiles] = useState<PendingFile[]>([]);
  // Opened from a page: optionally attach a picture of it (remembered, off at first).
  const [screenshot, setScreenshot] = useState(readAttachScreenshot);
  const offerScreenshot = Boolean(currentPage);
  // Another project picked: its Board's columns, types, people and labels differ; what's typed stays.
  const [forProject, setForProject] = useState(projectKey);
  if (projectKey !== forProject) {
    setForProject(projectKey);
    setColumnId(undefined);
    setTypeId(DEFAULT_TYPE);
    setAssignees([]);
    setLabels([]);
  }

  /** Closes at once: the task shows (and opens) before the server has saved it. */
  const submit = () => {
    const column = meta.columns.find((c) => c.id === columnId) ?? firstOpen;
    if (!title.trim() || !column) return;
    const { id } = createTaskNow({
      title: title.trim(),
      column,
      description: displayToMarkup(text.trim(), mentions),
      assignee_ids: assignees,
      label_ids: labels,
      contexts,
      type_id: typeId === DEFAULT_TYPE ? undefined : typeId,
      defaultTypeId: meta.types.find((t) => t.is_default)?.id,
    });
    onClose();
    onCreated?.(id);
    const picked = files.map((f) => f.file);
    // Files go up as the first message; that request waits for the task to exist. The page
    // screenshot leads it. The dialog is outside the captured area, so it isn't in the picture.
    if (offerScreenshot && screenshot) {
      void capturePage()
        .catch((error: unknown) => {
          console.error('[tasks] page screenshot failed', error);
          return null;
        })
        .then((shot) => {
          if (!shot) toast.error('Couldn’t take a screenshot of the page');
          const all = shot ? [shot, ...picked] : picked;
          if (all.length) attachFilesToTask(id, all);
        });
    } else if (picked.length) {
      attachFilesToTask(id, picked);
    }
  };

  const addFiles = (next: File[]) => {
    const ok = withinSizeLimit(next);
    setFiles((prev) => [
      ...prev,
      ...ok.map((file) => ({ key: pendingKey(file), file, attachmentId: null, uploading: false })),
    ]);
  };
  const drop = useFileDrop(addFiles);

  return (
    <>
      {hideHeading ? null : (
        <DialogHeader>
          <DialogTitle>{heading}</DialogTitle>
        </DialogHeader>
      )}
      <div
        className={cn(
          'space-y-3 rounded-md',
          drop.dragging && 'outline-2 outline-offset-4 outline-dashed outline-primary'
        )}
        {...drop.bind}
        onPaste={(e) => {
          if (e.defaultPrevented) return;
          const pasted = clipboardFiles(e.clipboardData);
          if (!pasted.length) return;
          e.preventDefault();
          addFiles(pasted);
        }}
      >
        <Input
          aria-label="Title"
          autoFocus
          value={title}
          placeholder="What needs doing?"
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
        />
        <MentionTextarea
          aria-label="Details"
          tagging
          value={text}
          onChange={setText}
          mentions={mentions}
          onMentionsChange={(next) => {
            const people = next.filter(
              (m) => m.type === 'user' && !mentions.some((p) => p.type === 'user' && p.id === m.id)
            );
            if (people.length) setAssignees((prev) => [...new Set([...prev, ...people.map((m) => m.id)])]);
            setMentions(next);
          }}
          members={meta.members}
          aiBuilder={false}
          rows={4}
          placeholder="Details (optional)… paste or drop screenshots, @ to mention someone or something, # to tag"
          onSubmit={() => submit()}
          onFiles={addFiles}
        />
        <PendingFiles items={files} onRemove={(item) => setFiles((prev) => prev.filter((f) => f.key !== item.key))} />
        {offerScreenshot ? (
          <label className="flex w-fit items-center gap-2 text-sm text-muted-foreground">
            <Checkbox
              checked={screenshot}
              onCheckedChange={(on) => {
                setScreenshot(on === true);
                saveAttachScreenshot(on === true);
              }}
            />
            Attach a screenshot of this page
          </label>
        ) : null}
        {boardStatus}
        <TaskFieldControls
          meta={meta}
          value={{ columnId: columnId ?? firstOpen?.id ?? '', typeId, assigneeIds: assignees, labelIds: labels }}
          onChange={(next) => {
            setColumnId(next.columnId);
            setTypeId(next.typeId);
            setAssignees(next.assigneeIds);
            setLabels(next.labelIds);
          }}
          typeLead={{ value: DEFAULT_TYPE, label: 'Default type' }}
        >
          <ContextPicker
            currentPage={
              currentPage && !contexts.some((c) => c.type === currentPage.type && c.id === currentPage.id)
                ? currentPage
                : null
            }
            onPick={(ref) =>
              setContexts((prev) =>
                prev.some((c) => c.type === ref.type && c.id === ref.id)
                  ? prev
                  : [...prev, { ...ref, title: ref.title ?? ref.id, url: '' }]
              )
            }
          >
            <Button variant="outline" size="sm">
              <Plus className="size-4" />
              Link
            </Button>
          </ContextPicker>
        </TaskFieldControls>
        {labels.length || contexts.length ? (
          <div className="flex flex-wrap gap-1.5">
            {labels.map((id) => {
              const label = meta.labels.find((l) => l.id === id);
              return label ? <LabelChip key={id} label={label} /> : null;
            })}
            {contexts.map((c) => (
              <ContextChip
                key={`${c.type}:${c.id}`}
                context={c}
                onRemove={() => setContexts((prev) => prev.filter((x) => !(x.type === c.type && x.id === c.id)))}
              />
            ))}
          </div>
        ) : null}
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={!title.trim() || Boolean(boardStatus)}
          disabledReason={boardStatus ? 'Waiting for the project’s Board' : 'Give it a title'}
          onClick={() => submit()}
        >
          Create task
        </Button>
      </DialogFooter>
    </>
  );
}
